// RewardsPage.jsx — Complete Final Version
// Features: OTP signup, referral ?ref= auto-capture, how it works,
//           milestone progress bar, health tips carousel,
//           WhatsApp share, customer name in header

import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

// VAPID public key — replace with value from CloudShell Block 1
const VAPID_PUBLIC_KEY = "BILDPC-p8iavipLd3fpAMhKXND1GjAkuHz4SQBhj2nWw6htMAF8CFsQ3hor-USxfsSpJWFcJzXsihgdHoyRM-Y8";

// Convert VAPID key to Uint8Array for browser push API
function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

const C = {
  deepBlue:   "#1a3a5c",
  midBlue:    "#2563a8",
  lightBlue:  "#dbeafe",
  cream:      "#fdf6e3",
  saffron:    "#f5a623",
  white:      "#ffffff",
  textDark:   "#0f2138",
  textMid:    "#4a6580",
  textLight:  "#8faec8",
  success:    "#16a34a",
  error:      "#dc2626",
  border:     "#c8ddf0",
};

const HEALTH_TIPS = [
  // Milk
  { icon: "🥛", product: "Milk",   fact: "1 glass of fresh milk gives you 30% of your daily calcium — stronger bones every morning." },
  { icon: "🥛", product: "Milk",   fact: "Warm milk before bed releases tryptophan, a natural sleep-inducing amino acid." },
  { icon: "🥛", product: "Milk",   fact: "Fresh local milk has up to 50% more nutrients than packaged milk sitting on shelves." },
  { icon: "🥛", product: "Milk",   fact: "Children who drink milk daily have significantly better bone density by age 18." },
  { icon: "🥛", product: "Milk",   fact: "Milk is one of the few foods that naturally contains Vitamin D, essential for immunity." },

  // Curd / Dahi
  { icon: "🥣", product: "Curd",   fact: "Fresh curd has live probiotics that improve gut health and boost immunity naturally." },
  { icon: "🥣", product: "Curd",   fact: "Eating curd daily reduces bloating and improves digestion within 2 weeks." },
  { icon: "🥣", product: "Curd",   fact: "Curd is richer in calcium than milk — 1 cup of curd = 30% of your daily calcium need." },
  { icon: "🥣", product: "Curd",   fact: "Homemade-style fresh curd helps reduce acidity and stomach inflammation." },
  { icon: "🥣", product: "Curd",   fact: "Curd keeps you fuller longer — great for weight management without feeling deprived." },

  // Paneer
  { icon: "🧀", product: "Paneer", fact: "100g of paneer has more protein than 2 eggs — perfect for vegetarians." },
  { icon: "🧀", product: "Paneer", fact: "Paneer is rich in casein protein, which releases slowly and keeps muscles fuelled for hours." },
  { icon: "🧀", product: "Paneer", fact: "Fresh paneer has zero preservatives and is one of the cleanest protein sources available." },
  { icon: "🧀", product: "Paneer", fact: "Paneer contains conjugated linoleic acid (CLA) that helps reduce body fat naturally." },
  { icon: "🧀", product: "Paneer", fact: "Growing children who eat paneer regularly have stronger teeth and better bone development." },

  // Cream / Malai
  { icon: "🍦", product: "Cream",  fact: "Fresh cream is a natural source of fat-soluble vitamins A, D, E and K." },
  { icon: "🍦", product: "Cream",  fact: "A small amount of fresh malai daily supports skin health and keeps it naturally moisturised." },
  { icon: "🍦", product: "Cream",  fact: "Fresh cream from whole milk contains healthy short-chain fatty acids good for your gut." },

  // Ghee
  { icon: "🧈", product: "Ghee",   fact: "Pure desi ghee is rich in butyric acid — one of the best fuels for gut health." },
  { icon: "🧈", product: "Ghee",   fact: "A teaspoon of ghee with warm water in the morning aids digestion and boosts metabolism." },
  { icon: "🧈", product: "Ghee",   fact: "Ghee has a high smoke point — the safest cooking fat for Indian cooking at high heat." },
  { icon: "🧈", product: "Ghee",   fact: "Unlike processed oils, pure ghee contains no trans fats and is heart-friendly in moderation." },
  { icon: "🧈", product: "Ghee",   fact: "Desi ghee is rich in omega-3 fatty acids that support brain health and memory." },

  // Lassi / Buttermilk
  { icon: "🥤", product: "Lassi",  fact: "A glass of lassi after meals is the best natural antacid — beats any medicine." },
  { icon: "🥤", product: "Lassi",  fact: "Buttermilk is low in calories but high in protein — perfect summer coolant and meal supplement." },
  { icon: "🥤", product: "Lassi",  fact: "Fresh lassi has live cultures that replenish gut bacteria lost due to heat or stress." },
];

const BIDS = { milkas: "biz-a63374ba-df71-48d5-b419-3447eedd6c8f" };

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Playfair+Display:wght@700;900&family=Noto+Sans+Devanagari:wght@400;600;700;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.cream}; font-family: 'Nunito', sans-serif; color: ${C.textDark}; min-height: 100vh; }
  .milkas-wrap { max-width: 420px; margin: 0 auto; min-height: 100vh; background: ${C.cream}; position: relative; overflow-x: hidden; }
  .milkas-wrap::before { content: ''; position: fixed; inset: 0; background-image: radial-gradient(circle, ${C.border} 1px, transparent 1px); background-size: 24px 24px; opacity: 0.4; pointer-events: none; z-index: 0; }
  .content { position: relative; z-index: 1; }

  .header { background: ${C.deepBlue}; padding: 20px 20px 28px; position: relative; overflow: hidden; }
  .header::after { content: ''; position: absolute; bottom: -16px; left: 0; right: 0; height: 32px; background: ${C.cream}; border-radius: 50% 50% 0 0 / 100% 100% 0 0; }
  .header-brand { display: flex; align-items: center; gap: 10px; }
  .header-logo { width: 36px; height: 36px; background: ${C.white}; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
  .header-name { font-size: 18px; font-weight: 800; color: ${C.white}; }
  .header-sub { font-size: 12px; color: ${C.lightBlue}; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; }

  .points-card { margin: 32px 20px 16px; background: ${C.deepBlue}; border-radius: 20px; padding: 28px 24px; text-align: center; position: relative; overflow: hidden; box-shadow: 0 8px 32px rgba(26,58,92,0.18); }
  .points-card::before { content: ''; position: absolute; top: -40px; right: -40px; width: 140px; height: 140px; background: rgba(255,255,255,0.05); border-radius: 50%; }
  .points-label { font-size: 11px; font-weight: 700; color: ${C.lightBlue}; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 8px; }
  .points-number { font-family: 'Playfair Display', serif; font-size: 72px; font-weight: 900; color: ${C.white}; line-height: 1; position: relative; z-index: 1; }
  .points-unit { font-size: 14px; font-weight: 700; color: ${C.saffron}; margin-top: 4px; letter-spacing: 1px; text-transform: uppercase; }
  .streak-badge { display: inline-flex; align-items: center; gap: 4px; background: rgba(245,166,35,0.15); border: 1px solid rgba(245,166,35,0.3); border-radius: 20px; padding: 4px 12px; font-size: 12px; font-weight: 700; color: ${C.saffron}; margin-top: 12px; }

  .section-title { font-size: 11px; font-weight: 800; color: ${C.textMid}; letter-spacing: 2px; text-transform: uppercase; padding: 0 20px; margin: 24px 0 10px; }

  .earn-card { margin: 0 20px 12px; background: ${C.white}; border-radius: 16px; padding: 18px 20px; border: 1.5px solid ${C.border}; box-shadow: 0 2px 12px rgba(26,58,92,0.06); }
  .earn-row { display: flex; align-items: center; gap: 14px; }
  .earn-icon { width: 44px; height: 44px; border-radius: 12px; background: ${C.lightBlue}; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
  .earn-title { font-size: 14px; font-weight: 800; color: ${C.textDark}; margin-bottom: 2px; }
  .earn-desc { font-size: 12px; color: ${C.textMid}; font-weight: 600; }
  .earn-pts { font-size: 18px; font-weight: 900; color: ${C.midBlue}; }
  .earn-pts span { font-size: 11px; color: ${C.textLight}; font-weight: 600; }

  .reward-grid { padding: 0 20px; display: flex; flex-direction: column; gap: 10px; margin-bottom: 10px; }
  .reward-card { background: ${C.white}; border-radius: 14px; padding: 16px 18px; border: 1.5px solid ${C.border}; display: flex; align-items: center; gap: 14px; }
  .reward-card.eligible { border-color: ${C.saffron}; background: linear-gradient(135deg, ${C.white} 0%, #fffbf0 100%); }
  .reward-icon { font-size: 28px; width: 44px; text-align: center; flex-shrink: 0; }

  .referral-card { margin: 0 20px; background: linear-gradient(135deg, ${C.deepBlue} 0%, ${C.midBlue} 100%); border-radius: 16px; padding: 20px; }
  .referral-code-box { background: rgba(255,255,255,0.12); border: 1.5px dashed rgba(255,255,255,0.3); border-radius: 10px; padding: 10px 16px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .referral-code { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: ${C.saffron}; letter-spacing: 2px; }

  .syncfield-section { background: ${C.white}; border-radius: 14px; padding: 16px; border: 1.5px solid ${C.border}; }
  .syncfield-label { font-size: 10px; font-weight: 700; color: ${C.textLight}; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 12px; }
  .syncfield-products { display: flex; gap: 8px; }
  .syncfield-product { flex: 1; background: ${C.cream}; border-radius: 10px; padding: 10px 8px; text-align: center; text-decoration: none; border: 1px solid ${C.border}; }
  .syncfield-product-icon { font-size: 20px; margin-bottom: 4px; }
  .syncfield-product-name { font-size: 10px; font-weight: 800; color: ${C.textDark}; line-height: 1.2; }

  .signup-wrap { padding: 32px 20px 20px; }
  .signup-card { background: ${C.white}; border-radius: 20px; padding: 28px 24px; border: 1.5px solid ${C.border}; box-shadow: 0 4px 24px rgba(26,58,92,0.08); }
  .signup-title { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 700; color: ${C.deepBlue}; margin-bottom: 6px; }
  .signup-sub { font-size: 13px; color: ${C.textMid}; font-weight: 600; margin-bottom: 24px; }
  .form-label { font-size: 11px; font-weight: 800; color: ${C.textMid}; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 6px; display: block; }
  .form-input { width: 100%; padding: 12px 16px; border: 2px solid ${C.border}; border-radius: 10px; font-size: 15px; font-weight: 600; font-family: 'Nunito', sans-serif; color: ${C.textDark}; background: ${C.cream}; outline: none; margin-bottom: 16px; }
  .form-input:focus { border-color: ${C.midBlue}; }
  .form-select { width: 100%; padding: 12px 16px; border: 2px solid ${C.border}; border-radius: 10px; font-size: 14px; font-weight: 600; font-family: 'Nunito', sans-serif; color: ${C.textDark}; background: ${C.cream}; outline: none; margin-bottom: 20px; appearance: none; }
  .primary-btn { width: 100%; padding: 14px; background: ${C.deepBlue}; color: ${C.white}; border: none; border-radius: 12px; font-size: 15px; font-weight: 800; font-family: 'Nunito', sans-serif; cursor: pointer; transition: background 0.2s; }
  .primary-btn:hover { background: ${C.midBlue}; }
  .primary-btn:disabled { background: ${C.border}; cursor: not-allowed; }

  .how-card { background: ${C.white}; border-radius: 16px; padding: 18px 20px; border: 1.5px solid ${C.border}; }
  .how-step { display: flex; align-items: center; gap: 14px; padding: 10px 0; border-bottom: 1px solid ${C.border}; }
  .how-step:last-child { border-bottom: none; padding-bottom: 0; }
  .how-step-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
  .how-step-title { font-size: 13px; font-weight: 800; color: ${C.textDark}; }
  .how-step-desc { font-size: 12px; color: ${C.textMid}; font-weight: 600; margin-top: 1px; }
  .how-step-pts { font-size: 14px; font-weight: 900; color: ${C.midBlue}; white-space: nowrap; flex-shrink: 0; }

  .milestone-card { margin: 0 20px 12px; background: ${C.white}; border-radius: 16px; padding: 18px 20px; border: 1.5px solid ${C.border}; }
  .milestone-bar-bg { background: ${C.border}; border-radius: 20px; height: 10px; margin: 10px 0 6px; overflow: hidden; }
  .milestone-bar-fill { height: 10px; border-radius: 20px; background: linear-gradient(90deg, ${C.midBlue}, ${C.saffron}); transition: width 0.6s ease; }

  .tip-card { margin: 0 20px 12px; background: linear-gradient(135deg, #e8f4fd, #dbeafe); border-radius: 16px; padding: 16px 18px; border: 1.5px solid ${C.border}; display: flex; align-items: flex-start; gap: 14px; cursor: pointer; }

  .offer-banner { margin: 0 20px 12px; border-radius: 16px; padding: 14px 18px; display: flex; align-items: center; gap: 14px; position: relative; overflow: hidden; border: 1.5px solid rgba(245,166,35,0.4); background: linear-gradient(135deg, #1a3a5c 0%, #2563a8 100%); }
  .offer-banner::before { content: ''; position: absolute; top: -20px; right: -20px; width: 80px; height: 80px; background: rgba(245,166,35,0.15); border-radius: 50%; }
  .offer-title { font-size: 10px; font-weight: 800; color: ${C.saffron}; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 3px; }
  .offer-text { font-size: 14px; font-weight: 800; color: ${C.white}; line-height: 1.3; }
  .offer-hindi { font-family: 'Noto Sans Devanagari', sans-serif; font-size: 12px; color: rgba(255,255,255,0.7); font-weight: 600; margin-top: 2px; }
  .offer-expires { font-size: 10px; color: rgba(255,255,255,0.5); font-weight: 600; margin-top: 4px; }

  .referral-status { margin: 0 20px 12px; background: ${C.white}; border-radius: 16px; padding: 16px 18px; border: 1.5px solid ${C.border}; }
  .ref-item { display: flex; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px solid ${C.border}; }
  .ref-item:last-child { border-bottom: none; padding-bottom: 0; }
  .ref-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 800; flex-shrink: 0; }
  .ref-info { flex: 1; }
  .ref-name { font-size: 13px; font-weight: 800; color: ${C.textDark}; }
  .ref-progress { font-size: 11px; color: ${C.textMid}; font-weight: 600; margin-top: 1px; }
  .ref-badge { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 20px; flex-shrink: 0; }

  .toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: ${C.deepBlue}; color: ${C.white}; padding: 12px 24px; border-radius: 30px; font-size: 13px; font-weight: 700; box-shadow: 0 4px 20px rgba(0,0,0,0.2); z-index: 999; white-space: nowrap; animation: toastIn 0.3s ease; }
  .toast.success { background: ${C.success}; }
  .toast.error { background: ${C.error}; }
  @keyframes toastIn { from { opacity:0; transform: translateX(-50%) translateY(12px); } to { opacity:1; transform: translateX(-50%) translateY(0); } }

  .loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 16px; }
  .loader { width: 40px; height: 40px; border: 3px solid ${C.border}; border-top-color: ${C.midBlue}; border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loader-text { font-size: 13px; color: ${C.textMid}; font-weight: 600; }
  .bottom-pad { height: 40px; }
`;

function rewardEmoji(name = "") {
  if (name.toLowerCase().includes("curd")) return "🥛";
  if (name.toLowerCase().includes("paneer")) return "🧀";
  if (name.toLowerCase().includes("milk")) return "🍼";
  if (name.toLowerCase().includes("lassi")) return "🥤";
  if (name.toLowerCase().includes("family")) return "👨‍👩‍👧‍👦";
  return "🎁";
}

function getNextMilestone(points, catalog) {
  if (!catalog) return null;
  const active = catalog.filter(r => r.active).sort((a, b) => a.points - b.points);
  const next = active.find(r => r.points > points);
  if (!next) return { done: true };
  const prev = active.filter(r => r.points <= points).pop();
  const base = prev ? prev.points : 0;
  const progress = Math.min(100, Math.round(((points - base) / (next.points - base)) * 100));
  return { done: false, next, needed: next.points - points, progress };
}

const HOW_STEPS = [
  { icon: "📍", bg: "#dbeafe", title: "Visit Daily",       desc: "Open rewards at Milkas, tap Earn",     pts: "+5 pts/day" },
  { icon: "🔥", bg: "#fef3c7", title: "7-Day Streak",      desc: "Visit 7 days in a row",               pts: "+20 bonus"  },
  { icon: "🎁", bg: "#fce7f3", title: "Redeem Rewards",    desc: "Ask staff to show QR → scan",         pts: "10AM–4PM"   },
  { icon: "👥", bg: "#dcfce7", title: "Refer & Earn",      desc: "Friend visits 3×  → you earn 30 pts", pts: "+30 pts"   },
];

// ── Referral Status Component ─────────────────────────────────────────────
function ReferralStatus({ businessId, phone, config, API }) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState({ referred: 0, verified: 0, max: 5, pts: 30, triggerAt: 3 });

  useEffect(() => {
    if (!businessId || !phone) return;
    fetch(`${API}/rewards/my-referrals?businessId=${businessId}&phone=${encodeURIComponent(phone)}`)
      .then(r => r.json())
      .then(d => {
        if (d.referrals) {
          setReferrals(d.referrals);
          setTotal({ referred: d.totalReferred, verified: d.totalVerified, max: d.maxReferrals, pts: d.pointsPerReferral, triggerAt: d.triggerAtVisit });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [businessId, phone]);

  if (loading || referrals.length === 0) return null;

  const C2 = { deepBlue:"#1a3a5c", midBlue:"#2563a8", saffron:"#f5a623", white:"#ffffff", textDark:"#0f2138", textMid:"#4a6580", success:"#16a34a", error:"#dc2626", border:"#c8ddf0", cream:"#fdf6e3" };

  return (
    <div>
      <div className="section-title" style={{ marginTop: 24 }}>Your Referrals ({total.referred}/{total.max})</div>
      <div className="referral-status">
        {referrals.map((ref, i) => {
          const verified = ref.status === "VERIFIED";
          const pending = ref.status === "PENDING";
          const visits = ref.visitCount || 0;
          const needed = ref.visitsNeeded || 0;
          const pct = Math.min(100, Math.round((visits / total.triggerAt) * 100));

          return (
            <div key={i} className="ref-item">
              <div className="ref-avatar" style={{ background: verified ? "#dcfce7" : "#fdf6e3", color: verified ? C2.success : C2.saffron }}>
                {ref.invitedName?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="ref-info">
                <div className="ref-name">{ref.invitedName}</div>
                <div className="ref-progress">
                  {verified
                    ? `Verified — you earned +${total.pts} pts`
                    : `${visits}/${total.triggerAt} visits done${needed > 0 ? ` — ${needed} more needed` : ""}`}
                </div>
                {pending && (
                  <div style={{ marginTop: 4, height: 4, background: C2.border, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: 4, width: `${pct}%`, background: C2.midBlue, borderRadius: 4, transition: "width 0.4s" }} />
                  </div>
                )}
              </div>
              <div className="ref-badge" style={{
                background: verified ? "#dcfce7" : pending ? "#fdf6e3" : "#f1f5f9",
                color: verified ? C2.success : pending ? C2.saffron : C2.textMid,
                border: `1px solid ${verified ? "#bbf7d0" : pending ? "#fde68a" : C2.border}`,
              }}>
                {verified ? "✅ Done" : pending ? "⏳ Pending" : "—"}
              </div>
            </div>
          );
        })}
        {total.referred === 0 && (
          <div style={{ fontSize: 13, color: "#4a6580", fontWeight: 600, textAlign: "center", padding: "8px 0" }}>
            No referrals yet. Share your code below!
          </div>
        )}
      </div>
    </div>
  );
}

export default function RewardsPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || null;

  const [phase, setPhase] = useState("loading");
  const [config, setConfig] = useState(null);
  const [member, setMember] = useState(null);
  const [toast, setToast] = useState(null);
  const [tipIndex, setTipIndex] = useState(0);

  const [formPhone, setFormPhone] = useState("");
  const [otp, setOtp] = useState(["","","","","",""]);
  const [confirmResult, setConfirmResult] = useState(null);
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const recaptchaRef = useRef(null);
  const otpRefs = useRef([]);

  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("HOUSEHOLD");
  const [submitting, setSubmitting] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [earningVisit, setEarningVisit] = useState(false);

  const businessId = BIDS[slug] || null;

  // Read stored referral code from localStorage — persists across sessions
  const storedRef = businessId ? localStorage.getItem(`milkas_ref_${businessId}`) : null;
  const effectiveRef = refCode || storedRef || null;

  const [pushEnabled, setPushEnabled] = useState(
    businessId ? localStorage.getItem(`milkas_push_${businessId}`) === "1" : false
  );
  const [pushAsking, setPushAsking] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTipIndex(i => (i + 1) % HEALTH_TIPS.length), 8000);
    return () => clearInterval(t);
  }, []);

  // Register service worker
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js")
        .then(reg => {
          // Check if already subscribed
          reg.pushManager.getSubscription().then(sub => {
            if (sub) setPushEnabled(true);
          });
        })
        .catch(err => console.warn("SW registration failed:", err));
    }
  }, []);

  // Persist referral code to localStorage as soon as we see it in URL
  useEffect(() => {
    if (refCode && businessId) {
      localStorage.setItem(`milkas_ref_${businessId}`, refCode);
    }
  }, [refCode, businessId]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (!businessId) { setPhase("error"); return; }
    fetchConfig();
  }, [businessId]);

  async function fetchConfig() {
    try {
      const r = await fetch(`${API}/rewards/config?businessId=${businessId}`);
      const d = await r.json();
      if (!r.ok) { setPhase("error"); return; }
      setConfig(d);
      const saved = localStorage.getItem(`milkas_member_${businessId}`);
      if (saved) await refreshMember(JSON.parse(saved).phone);
      else setPhase("signup_phone");
    } catch { setPhase("error"); }
  }

  async function refreshMember(phone) {
    try {
      const r = await fetch(`${API}/rewards/member?businessId=${businessId}&phone=${encodeURIComponent(phone)}`);
      if (r.ok) {
        const d = await r.json();
        setMember(d.member);
        localStorage.setItem(`milkas_member_${businessId}`, JSON.stringify(d.member));
        setPhase("dashboard");
      } else {
        localStorage.removeItem(`milkas_member_${businessId}`);
        setPhase("signup_phone");
      }
    } catch { setPhase("signup_phone"); }
  }

  async function handleSendOtp() {
    const digits = formPhone.replace(/\D/g, "");
    if (digits.length !== 10) { showToast("Enter a valid 10-digit mobile number", "error"); return; }
    setSendingOtp(true);
    try {
      if (recaptchaRef.current) { recaptchaRef.current.clear(); recaptchaRef.current = null; }
      recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible", callback: () => {} });
      await recaptchaRef.current.render();
      const result = await signInWithPhoneNumber(auth, `+91${digits}`, recaptchaRef.current);
      setConfirmResult(result);
      setOtp(["","","","","",""]);
      setPhase("signup_otp");
      setResendCooldown(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      console.error(err);
      showToast("Failed to send OTP. Please try again.", "error");
      recaptchaRef.current?.clear(); recaptchaRef.current = null;
    } finally { setSendingOtp(false); }
  }

  async function handleVerifyOtp() {
    const code = otp.join("");
    if (code.length !== 6) { showToast("Enter the 6-digit OTP", "error"); return; }
    setVerifyingOtp(true);
    try {
      await confirmResult.confirm(code);
      const phone = `+91${formPhone.replace(/\D/g, "")}`;
      setVerifiedPhone(phone);
      const r = await fetch(`${API}/rewards/member?businessId=${businessId}&phone=${encodeURIComponent(phone)}`);
      if (r.ok) {
        const d = await r.json();
        setMember(d.member);
        localStorage.setItem(`milkas_member_${businessId}`, JSON.stringify(d.member));
        setPhase("dashboard");
        showToast(`Welcome back, ${d.member.name}! 🎉`, "success");
      } else {
        setPhase("signup_details");
      }
    } catch (err) {
      console.error(err);
      showToast("Invalid OTP. Please try again.", "error");
      setOtp(["","","","","",""]); setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } finally { setVerifyingOtp(false); }
  }

  function handleOtpChange(val, idx) {
    const v = val.replace(/\D/g, "").slice(0, 1);
    const next = [...otp]; next[idx] = v; setOtp(next);
    if (v && idx < 5) otpRefs.current[idx + 1]?.focus();
  }

  function handleOtpPaste(e) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) { setOtp(pasted.split("")); otpRefs.current[5]?.focus(); }
  }

  async function handleJoin() {
    if (!formName.trim()) { showToast("Please enter your name", "error"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/rewards/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, phone: verifiedPhone, name: formName.trim(), customerType: formType, referredBy: effectiveRef }),
      });
      const d = await r.json();
      if (r.ok || r.status === 201 || r.status === 200) {
        setMember(d.member);
        localStorage.setItem(`milkas_member_${businessId}`, JSON.stringify(d.member));
        localStorage.removeItem(`milkas_ref_${businessId}`); // Clear ref after use
        setPhase("dashboard");
        showToast(`Welcome, ${d.member.name}! 🎉`, "success");
      } else { showToast(d.error || "Something went wrong", "error"); }
    } catch { showToast("Network error. Try again.", "error"); }
    finally { setSubmitting(false); }
  }

  async function handleEarnVisit() {
    if (!navigator.geolocation) { showToast("Location required", "error"); return; }
    setEarningVisit(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await fetch(`${API}/rewards/earn/visit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ businessId, phone: member.phone, gpsLat: pos.coords.latitude, gpsLng: pos.coords.longitude }),
          });
          const d = await r.json();
          if (r.ok) { showToast(d.message || `+${d.pointsEarned} points!`, "success"); await refreshMember(member.phone); }
          else showToast(d.error || "Could not earn points", "error");
        } catch { showToast("Network error", "error"); }
        finally { setEarningVisit(false); }
      },
      () => { showToast("Please allow location access", "error"); setEarningVisit(false); },
      { timeout: 8000, maximumAge: 60000 }
    );
  }

  async function handleEnablePush() {
    if (!businessId || pushAsking) return;
    setPushAsking(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        showToast("Please allow notifications to get offer alerts", "error");
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      // Save subscription to backend
      const r = await fetch(`${API}/rewards/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId,
          phone: member?.phone || null,
          subscription: sub.toJSON(),
        }),
      });
      if (r.ok) {
        setPushEnabled(true);
        localStorage.setItem(`milkas_push_${businessId}`, "1");
        showToast("Notifications enabled! You will get offer alerts 🔔", "success");
      }
    } catch (err) {
      console.error("Push subscribe error:", err);
      showToast("Could not enable notifications", "error");
    } finally {
      setPushAsking(false);
    }
  }

  function showToast(msg, type = "info") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }

  function handleWhatsAppShare() {
    const link = `https://qkard.app/rewards/milkas${member?.referralCode ? `?ref=${member.referralCode}` : ""}`;
    const msg = `🥛 Join Milkas Dairy Rewards!\n\nEarn free milk, curd & more on every visit.\n\nJoin here: ${link}\n\nMy code: ${member?.referralCode || ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  }

  const tip = HEALTH_TIPS[tipIndex];
  const milestone = getNextMilestone(member?.points || 0, config?.redemptionCatalog);

  return (
    <>
      <style>{styles}</style>
      <div className="milkas-wrap">
        <div className="content">

          {/* HEADER */}
          <div className="header">
            <div className="header-brand">
              <div className="header-logo">🥛</div>
              <div style={{ flex: 1 }}>
                <div className="header-name">Milkas Dairy</div>
                <div className="header-sub">Rewards Program</div>
              </div>
              {member && (
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>Hi,</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.white, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {member.name?.split(" ")[0]}
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", fontWeight: 600, marginBottom: 4 }}>
                    {member.phone?.replace("+91","").replace(/(\d{5})(\d{5})/,"$1 $2")}
                  </div>
                  <button
                    onClick={() => {
                      localStorage.removeItem(`milkas_member_${businessId}`);
                      setMember(null);
                      setPhase("signup_phone");
                    }}
                    style={{
                      background: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 6, padding: "2px 8px",
                      fontSize: 9, fontWeight: 700,
                      color: "rgba(255,255,255,0.6)",
                      cursor: "pointer", fontFamily: "inherit",
                      letterSpacing: 0.5,
                    }}
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* LOADING */}
          {phase === "loading" && <div className="loader-wrap"><div className="loader"/><div className="loader-text">Loading your rewards…</div></div>}

          {/* ERROR */}
          {phase === "error" && <div className="loader-wrap"><div style={{fontSize:40}}>😕</div><div className="loader-text">Shop not found or rewards inactive.</div></div>}

          {/* SIGNUP: PHONE */}
          {phase === "signup_phone" && (
            <div className="signup-wrap">

              {/* Hero section — visible before signup */}
              <div style={{
                background: "linear-gradient(135deg, #1a3a5c 0%, #2563a8 100%)",
                borderRadius: 20, padding: "24px 20px 20px",
                marginBottom: 16, position: "relative", overflow: "hidden",
              }}>
                {/* decorative circle */}
                <div style={{ position:"absolute", top:-30, right:-20, width:100, height:100, background:"rgba(245,166,35,0.1)", borderRadius:"50%" }} />
                <div style={{ position:"absolute", bottom:-20, left:-10, width:70, height:70, background:"rgba(255,255,255,0.05)", borderRadius:"50%" }} />

                {/* Free badge */}
                <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(245,166,35,0.15)", border:"1px solid rgba(245,166,35,0.3)", borderRadius:20, padding:"4px 12px", marginBottom:12 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:"#f5a623", letterSpacing:1, textTransform:"uppercase" }}>✨ बिल्कुल मुफ़्त — Completely Free</span>
                </div>

                {/* Main headline */}
                <div style={{ marginBottom:8, position:"relative", zIndex:1 }}>
                  <div style={{ fontFamily:"'Noto Sans Devanagari', 'Playfair Display', serif", fontSize:30, fontWeight:900, color:"#ffffff", lineHeight:1.1 }}>
                    रोज़ आएं,
                  </div>
                  <div style={{ fontFamily:"'Playfair Display', serif", fontSize:32, fontWeight:900, lineHeight:1.1 }}>
                    <span style={{ color:"#f5a623" }}>FREE</span>
                    <span style={{ color:"#ffffff", fontFamily:"'Noto Sans Devanagari', sans-serif", fontSize:28 }}> पाएं!</span>
                  </div>
                </div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", fontWeight:600, marginBottom:20, lineHeight:1.5 }}>
                  Earn points on every visit.<br/>
                  Redeem FREE milk, curd &amp; more — no app needed.
                </div>

                {/* Reward pills */}
                <div style={{ display:"flex", gap:8, marginBottom:4 }}>
                  {[
                    { pts:"100", hi:"FREE दही", en:"Curd 200g" },
                    { pts:"150", hi:"FREE दूध", en:"Milk 500ml" },
                    { pts:"250", hi:"FREE दूध", en:"Milk 1L" },
                  ].map(r => (
                    <div key={r.pts} style={{ flex:1, background:"rgba(255,255,255,0.12)", borderRadius:12, padding:"10px 6px", textAlign:"center", border:"1.5px solid rgba(255,255,255,0.2)" }}>
                      <div style={{ fontSize:16, fontWeight:900, color:"#f5a623", lineHeight:1 }}>{r.pts}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.5)", fontWeight:600, marginBottom:2 }}>pts =</div>
                      <div style={{ fontSize:13, fontWeight:900, color:"#ffffff", lineHeight:1.2, fontFamily:"'Noto Sans Devanagari', sans-serif" }}>{r.hi}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.55)", fontWeight:600, marginTop:2 }}>{r.en}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* How it works — visible before signup */}
              <div className="how-card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.deepBlue, marginBottom: 12 }}>How Milkas Rewards Works</div>
                {HOW_STEPS.map(s => (
                  <div key={s.title} className="how-step">
                    <div className="how-step-icon" style={{ background: s.bg }}>{s.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div className="how-step-title">{s.title}</div>
                      <div className="how-step-desc">{s.desc}</div>
                    </div>
                    <div className="how-step-pts">{s.pts}</div>
                  </div>
                ))}
              </div>

              <div className="signup-card">
                <div className="signup-title">Join Milkas Rewards</div>
                <div className="signup-sub">Earn free milk, curd & more on every visit.</div>
                {effectiveRef && (
                  <div style={{ background: "#dcfce7", borderRadius: 10, padding: "8px 12px", fontSize: 12, fontWeight: 700, color: C.success, marginBottom: 16 }}>
                    🎉 Referral code <strong>{effectiveRef}</strong> applied! You'll get bonus points on joining.
                  </div>
                )}
                <label className="form-label">Mobile Number</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <div style={{ padding: "12px 14px", background: C.lightBlue, borderRadius: 10, fontSize: 15, fontWeight: 700, color: C.deepBlue, border: `2px solid ${C.border}`, flexShrink: 0 }}>+91</div>
                  <input className="form-input" style={{ marginBottom: 0, flex: 1 }} placeholder="10-digit mobile number"
                    value={formPhone} onChange={e => setFormPhone(e.target.value.replace(/\D/g,"").slice(0,10))}
                    type="tel" maxLength={10} onKeyDown={e => e.key==="Enter" && handleSendOtp()} />
                </div>
                <button className="primary-btn" onClick={handleSendOtp} disabled={sendingOtp || formPhone.replace(/\D/g,"").length!==10}>
                  {sendingOtp ? "Sending OTP…" : "Send OTP →"}
                </button>
                <div id="recaptcha-container" />
              </div>

              <div style={{ marginTop: 20 }}>
                <div className="syncfield-section">
                  <div className="syncfield-label">Also from SyncField</div>
                  <div className="syncfield-products">
                    <a href="https://qkard.app" className="syncfield-product"><div className="syncfield-product-icon">🏪</div><div className="syncfield-product-name">QKARD</div></a>
                    <a href="https://app.syncvault.in" className="syncfield-product"><div className="syncfield-product-icon">📄</div><div className="syncfield-product-name">DocuVault</div></a>
                    <a href="https://hotel.syncvault.in" className="syncfield-product"><div className="syncfield-product-icon">🏨</div><div className="syncfield-product-name">SyncVault Hotel</div></a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SIGNUP: OTP */}
          {phase === "signup_otp" && (
            <div className="signup-wrap">
              <div className="signup-card">
                <div style={{ textAlign: "center", fontSize: 36, marginBottom: 12 }}>💬</div>
                <div className="signup-title" style={{ textAlign: "center" }}>Verify Your Number</div>
                <div className="signup-sub" style={{ textAlign: "center" }}>OTP sent to +91 {formPhone}<br/><span style={{ fontSize: 12 }}>Check your SMS</span></div>
                <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 20 }} onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input key={i} ref={el => otpRefs.current[i]=el}
                      type="text" inputMode="numeric" maxLength={1} value={digit}
                      onChange={e => handleOtpChange(e.target.value, i)}
                      onKeyDown={e => { if(e.key==="Backspace"&&!otp[i]&&i>0) otpRefs.current[i-1]?.focus(); if(e.key==="Enter"&&otp.join("").length===6) handleVerifyOtp(); }}
                      autoFocus={i===0}
                      style={{ width:44, height:52, textAlign:"center", fontSize:22, fontWeight:800, border:`2px solid ${digit?C.midBlue:C.border}`, borderRadius:10, outline:"none", background:C.cream, color:C.deepBlue, fontFamily:"inherit" }}
                    />
                  ))}
                </div>
                <button className="primary-btn" onClick={handleVerifyOtp} disabled={verifyingOtp||otp.join("").length!==6}>
                  {verifyingOtp ? "Verifying…" : "Verify OTP →"}
                </button>
                <div style={{ textAlign:"center", marginTop:14, fontSize:13, color:C.textMid, fontWeight:600 }}>
                  {resendCooldown>0 ? `Resend in ${resendCooldown}s` : <span style={{color:C.midBlue,cursor:"pointer",fontWeight:700}} onClick={handleSendOtp}>Resend OTP</span>}
                  {" · "}
                  <span style={{color:C.midBlue,cursor:"pointer",fontWeight:700}} onClick={()=>{setPhase("signup_phone");setOtp(["","","","","",""]);}}>Change number</span>
                </div>
              </div>
            </div>
          )}

          {/* SIGNUP: DETAILS */}
          {phase === "signup_details" && (
            <div className="signup-wrap">
              <div className="signup-card">
                <div style={{ textAlign:"center", fontSize:36, marginBottom:12 }}>✅</div>
                <div className="signup-title">One Last Step</div>
                <div className="signup-sub">Number verified! Tell us your name.</div>
                <label className="form-label">Your Name</label>
                <input className="form-input" placeholder="e.g. Ravi Kumar" value={formName} onChange={e=>setFormName(e.target.value)} autoFocus />
                <label className="form-label">I am a</label>
                <select className="form-select" value={formType} onChange={e=>setFormType(e.target.value)}>
                  <option value="HOUSEHOLD">Household / Family</option>
                  <option value="RESTAURANT">Restaurant / Hotel / Café</option>
                  <option value="CATERER">Caterer / Event Business</option>
                  <option value="RESELLER">Reseller / Shop Partner</option>
                </select>
                <button className="primary-btn" onClick={handleJoin} disabled={submitting}>
                  {submitting ? "Joining…" : "Join & Start Earning 🎉"}
                </button>
              </div>
            </div>
          )}

          {/* DASHBOARD */}
          {phase === "dashboard" && member && config && (
            <>
              {/* Points */}
              <div className="points-card">
                <div className="points-label">Your Balance</div>
                <div className="points-number">{member.points}</div>
                <div className="points-unit">Milkas Points</div>
                {member.visitStreak > 1 && <div className="streak-badge">🔥 {member.visitStreak} day streak</div>}
              </div>

              {/* Today's Special Offer */}
              {config.todayOffer && (
                <div className="offer-banner">
                  <div style={{ fontSize: 28, flexShrink: 0, position: "relative", zIndex: 1 }}>🎉</div>
                  <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
                    <div className="offer-title">Aaj ka Special</div>
                    <div className="offer-text">{config.todayOffer.text}</div>
                    {config.todayOffer.hindiText && (
                      <div className="offer-hindi">{config.todayOffer.hindiText}</div>
                    )}
                    <div className="offer-expires">
                      Valid until {new Date(config.todayOffer.expiresAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}
                    </div>
                  </div>
                </div>
              )}

              {/* Milestone */}
              {milestone && !milestone.done && (
                <div className="milestone-card">
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ fontSize:13, fontWeight:800, color:C.textDark }}>{rewardEmoji(milestone.next.name)} Next: {milestone.next.name}</div>
                    <div style={{ fontSize:12, fontWeight:700, color:C.midBlue }}>{milestone.needed} pts away</div>
                  </div>
                  <div className="milestone-bar-bg"><div className="milestone-bar-fill" style={{ width:`${milestone.progress}%` }}/></div>
                  <div style={{ fontSize:11, color:C.textLight, fontWeight:600 }}>{member.points} / {milestone.next.points} points</div>
                </div>
              )}
              {milestone?.done && (
                <div className="milestone-card" style={{ background:"#dcfce7", borderColor:C.success }}>
                  <div style={{ fontSize:13, fontWeight:800, color:C.success }}>🎉 You can redeem all rewards! Visit between 10AM–4PM and ask staff to show QR.</div>
                </div>
              )}

              {/* Health tip */}
              <div className="tip-card" onClick={()=>setTipIndex(i=>(i+1)%HEALTH_TIPS.length)}>
                <div style={{ fontSize:28, flexShrink:0 }}>{tip.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                    <div style={{ fontSize:10, fontWeight:800, color:C.textMid, letterSpacing:2, textTransform:"uppercase" }}>Dairy Fact</div>
                    <div style={{ fontSize:10, fontWeight:800, color:C.white, background:C.midBlue, borderRadius:20, padding:"2px 8px", letterSpacing:0.5 }}>{tip.product}</div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.deepBlue, lineHeight:1.4 }}>{tip.fact}</div>
                  <div style={{ fontSize:10, color:C.textLight, fontWeight:600, marginTop:4 }}>Tap for next · {tipIndex+1}/{HEALTH_TIPS.length} →</div>
                </div>
              </div>

              {/* Push notification prompt */}
              {"Notification" in window && (
                <div style={{
                  margin:"0 20px 12px",
                  background: pushEnabled ? "#dcfce7" : C.white,
                  borderRadius:16, padding:"14px 18px",
                  border: pushEnabled ? "1.5px solid #bbf7d0" : `1.5px solid ${C.border}`,
                  display:"flex", alignItems:"center", gap:14,
                  transition:"background 0.3s, border 0.3s",
                }}>
                  <div style={{ fontSize:28, flexShrink:0 }}>{pushEnabled ? "✅" : "🔔"}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:800, color: pushEnabled ? "#166534" : C.textDark, marginBottom:2 }}>
                      {pushEnabled ? "Offer Alerts Enabled!" : "Get Offer Alerts"}
                    </div>
                    <div style={{ fontSize:12, color: pushEnabled ? "#16a34a" : C.textMid, fontWeight:600 }}>
                      {pushEnabled
                        ? "You will be notified when Milkas posts a special offer 🎉"
                        : "Know instantly when Milkas posts a special offer"}
                    </div>
                  </div>
                  {!pushEnabled && (
                    <button onClick={handleEnablePush} disabled={pushAsking}
                      style={{ padding:"8px 14px", background: pushAsking ? C.border : C.deepBlue, color:C.white, border:"none", borderRadius:10, fontSize:12, fontWeight:800, cursor: pushAsking ? "not-allowed" : "pointer", fontFamily:"inherit", flexShrink:0, transition:"background 0.2s" }}>
                      {pushAsking ? "Enabling…" : "Enable"}
                    </button>
                  )}
                </div>
              )}

              {/* Earn */}
              <div className="section-title">Earn Points</div>
              <div className="earn-card">
                <div className="earn-row">
                  <div className="earn-icon">📍</div>
                  <div style={{ flex:1 }}>
                    <div className="earn-title">Visit Points</div>
                    <div className="earn-desc">Scan once per day at Milkas</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div className="earn-pts">+{config.pointValues?.visit||5} <span>pts</span></div>
                  </div>
                </div>
                <button className="primary-btn" style={{ marginTop:14, padding:"11px" }} onClick={handleEarnVisit} disabled={earningVisit}>
                  {earningVisit ? "Getting location…" : "Tap to Earn Visit Points"}
                </button>
              </div>

              {/* How it works */}
              <div className="section-title">How It Works</div>
              <div className="how-card" style={{ margin:"0 20px 12px" }}>
                {HOW_STEPS.map(s => (
                  <div key={s.title} className="how-step">
                    <div className="how-step-icon" style={{ background:s.bg }}>{s.icon}</div>
                    <div style={{ flex:1 }}>
                      <div className="how-step-title">{s.title}</div>
                      <div className="how-step-desc">{s.desc}</div>
                    </div>
                    <div className="how-step-pts">{s.pts}</div>
                  </div>
                ))}
              </div>

              {/* Rewards */}
              <div className="section-title">Redeem Rewards</div>
              <div style={{ padding:"0 20px 6px", fontSize:12, color:C.textMid, fontWeight:600 }}>
                Ask staff to show QR · {config.redemptionWindow?.startHour}:00 AM – {config.redemptionWindow?.endHour}:00 PM
              </div>
              <div className="reward-grid">
                {config.redemptionCatalog?.map(reward => {
                  const eligible = member.points >= reward.points;
                  return (
                    <div key={reward.rewardId} className={`reward-card ${eligible?"eligible":""}`}>
                      <div className="reward-icon">{rewardEmoji(reward.name)}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:800, color:C.textDark }}>{reward.name}</div>
                        <div style={{ fontSize:12, color:eligible?C.success:C.textMid, fontWeight:600, marginTop:2 }}>
                          {eligible ? "✅ Eligible to redeem!" : `Need ${reward.points-member.points} more points`}
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:13, fontWeight:900, color:C.saffron }}>{reward.points} pts</div>
                        <div style={{ fontSize:10, fontWeight:700, marginTop:2, color:eligible?C.success:C.textLight }}>{eligible?"ELIGIBLE":"🔒"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ padding:"4px 20px 0" }}>
                <div style={{ background:C.lightBlue, borderRadius:12, padding:"14px 16px", fontSize:13, color:C.deepBlue, fontWeight:700, textAlign:"center", border:`1.5px solid ${C.border}` }}>
                  To redeem → Visit Milkas between 10 AM–4 PM<br/>
                  <span style={{ fontWeight:800 }}>Ask staff to show QR → scan → collect reward</span>
                </div>
              </div>

              {/* Referral */}
              <div className="section-title">Refer & Earn</div>
              <div className="referral-card">
                <div style={{ fontSize:13, fontWeight:800, color:"#dbeafe", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Your Referral Code</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.8)", marginBottom:14, fontWeight:600 }}>
                  Share with friends &amp; family. They visit 3 times → you earn {config.referralRules?.pointsPerReferral || 30} points. Max {config.referralRules?.maxReferrals || 5} referrals.
                </div>
                <div className="referral-code-box">
                  <div className="referral-code">{member.referralCode}</div>
                  <button onClick={()=>{navigator.clipboard?.writeText(member.referralCode);showToast("Code copied!","success");}}
                    style={{ background:C.saffron, color:C.deepBlue, border:"none", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                    Copy
                  </button>
                </div>
                <button onClick={handleWhatsAppShare}
                  style={{ width:"100%", padding:"12px", background:"#25D366", color:C.white, border:"none", borderRadius:10, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                  📱 Share on WhatsApp
                </button>
              </div>

              {/* Referral status */}
              <ReferralStatus businessId={businessId} phone={member.phone} config={config} API={API} />

              {/* SyncField */}
              <div style={{ margin:"24px 20px 0" }}>
                <div className="syncfield-section">
                  <div className="syncfield-label">Also from SyncField</div>
                  <div className="syncfield-products">
                    <a href="https://qkard.app" className="syncfield-product"><div className="syncfield-product-icon">🏪</div><div className="syncfield-product-name">QKARD</div></a>
                    <a href="https://app.syncvault.in" className="syncfield-product"><div className="syncfield-product-icon">📄</div><div className="syncfield-product-name">DocuVault</div></a>
                    <a href="https://hotel.syncvault.in" className="syncfield-product"><div className="syncfield-product-icon">🏨</div><div className="syncfield-product-name">SyncVault Hotel</div></a>
                  </div>
                </div>
              </div>
              <div className="bottom-pad"/>
            </>
          )}
        </div>
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
